import { useState, useCallback } from "react";
import {
  _get_bill_providers,
  _validate_bill_payment,
  _process_bill_payment,
  _get_bill_history,
  _get_bill_plans,
} from "../common/axios_services";
import { showErrorNotification } from "../common/utils/notification";

const useBillPayments = (navigate) => {
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState({});
  const [billHistory, setBillHistory] = useState([]);

  const fetchBillProviders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await _get_bill_providers();
      setProviders(response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, navigate);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const validateBillPayment = useCallback(
    async (validationData) => {
      try {
        setLoading(true);
        const response = await _validate_bill_payment(validationData);
        return response.data;
      } catch (error) {
        handleApiError(error, navigate);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const processBillPayment = useCallback(
    async (paymentData) => {
      try {
        setLoading(true);
        const response = await _process_bill_payment(paymentData);
        return response.data;
      } catch (error) {
        handleApiError(error, navigate);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchBillHistory = useCallback(
    async (page = 1, limit = 20) => {
      try {
        setLoading(true);
        const response = await _get_bill_history({ page, limit });
        setBillHistory(response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, navigate);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchBillPlans = useCallback(
    async (billerId) => {
      try {
        setLoading(true);
        const response = await _get_bill_plans(billerId);
        const plansData = response.data?.data?.data || [];

        const formattedPlans = Array.isArray(plansData)
          ? plansData
              .map((item) => ({
                id: item.id,
                name: item.attributes?.name || item.name || "Plan",
                slug: item.attributes?.slug || item.slug,
                amount: item.attributes?.price?.minimumAmount || 0,
                minAmount: item.attributes?.price?.minimumAmount || 0,
                maxAmount: item.attributes?.price?.maximumAmount || 0,
                priceType: item.attributes?.price?.type || "Fixed",
              }))
              .sort((a, b) => a.amount - b.amount)
          : [];

        return formattedPlans;
      } catch (error) {
        handleApiError(error, navigate);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return {
    loading,
    providers,
    billHistory,
    fetchBillProviders,
    validateBillPayment,
    processBillPayment,
    fetchBillHistory,
    fetchBillPlans,
  };
};

const handleApiError = (err, navigate) => {
  showErrorNotification(
    err?.response?.data?.message || err?.message || "An error occurred",
  );
  throw new Error(err);
};

export default useBillPayments;
